import React from 'react';
import { Modal, Loader, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { ReportView, SessionCashCountView, ZReportPrintRequest } from 'shared-models/browser';
import { expectedCashFromReport } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { ReportPrinterPickModal } from '../reports/ReportPrinterPickModal';
import { CashCountModal } from '../session-cash-count/CashCountModal';
import { sessionCashCountService } from '../../../../services/sessions/session-cash-count.service';
import { SessionView } from '../../../../services/sessions/sessions.service';
import type { SessionCloseOutcome } from '../../../../services/sessions/sessions.service';
import { reportsService } from '../../../../services/reports/reports.service';
import { printersService } from '../../../../services/printers/printers.service';
import type { PrinterView } from '../../../../services/printers/printers.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { receiptPdfBase64 } from '../../../DashboardPage/components/BreakdownRowModal/summary-receipt-pdf.util';
import { formatMoney } from '../../../../utils/money';
import { PosReportView } from '../reports/PosReportView';
import { REPORT_PRINTER_STORAGE_KEY } from '../reports/auto-close-z-print.utils';
import { resolveReceiptPrintTarget } from '../../hooks/fixed-printer.util';
import classes from './session-close.module.scss';

type SessionControlsSectionProps = {
  activeSession: SessionView | null;
  isLoadingSession: boolean;
  isSavingSession: boolean;
  hasEmployeeSession: boolean;
  shiftCloseCashBalancesEnabled?: boolean;
  zReportEmailRecipients?: string[];
  orderCount?: number;
  salesTotal?: number;
  openOrderCount?: number;
  tenantId: string;
  employeeSession: string;
  onCloseSession: (payload?: {
    cashDrawerPrinterId?: number;
    closingCash?: number;
    nextOpeningCash?: number;
    zReportPrint?: ZReportPrintRequest;
  }) => Promise<SessionCloseOutcome | void> | void;
  onGoToOrders: () => void;
  onCancel: () => void;
  // Holds the shell's deferred session-closed refresh while the post-close Z print is in flight.
  onZPrintInFlightChange?: (inFlight: boolean) => void;
};

// Architectural summary: Session-close panel for manager operations. See docs/function-library/web/contexts-pages.md#pages-posshellpage-components-sessioncontrolssection.
const SessionControlsSectionComponent: React.FC<SessionControlsSectionProps> = ({
  activeSession,
  isLoadingSession,
  isSavingSession,
  hasEmployeeSession,
  shiftCloseCashBalancesEnabled = false,
  zReportEmailRecipients = [],
  orderCount = 0,
  salesTotal = 0,
  openOrderCount = 0,
  tenantId,
  employeeSession,
  onCloseSession,
  onGoToOrders,
  onCancel,
  onZPrintInFlightChange,
}) => {
  const [showOpenOrdersGuard, setShowOpenOrdersGuard] = React.useState(false);
  // Live open-order count from a mid-flow close conflict; overrides the possibly-stale prop.
  const [conflictOpenOrderCount, setConflictOpenOrderCount] = React.useState(0);
  const [zReport, setZReport] = React.useState<ReportView | null>(null);
  const [loadingZ, setLoadingZ] = React.useState(false);
  const [pickerPrinters, setPickerPrinters] = React.useState<PrinterView[] | null>(null);
  const [pickerMode, setPickerMode] = React.useState<'print-only' | 'close-with-print' | null>(null);
  const [closeDialogOpen, setCloseDialogOpen] = React.useState(false);
  const [closingCashInput, setClosingCashInput] = React.useState('');
  const [nextOpeningCashInput, setNextOpeningCashInput] = React.useState('');
  const [cashCountOpen, setCashCountOpen] = React.useState(false);
  const [cashCount, setCashCount] = React.useState<SessionCashCountView | null>(null);
  const printRef = React.useRef<HTMLDivElement>(null);
  const closingRef = React.useRef(false);
  const prefillDoneRef = React.useRef(false);

  const NO_SELECTION_INPUT_PROPS = {
    onContextMenu: (event: React.MouseEvent<HTMLInputElement>) => event.preventDefault(),
    onSelect: (event: React.SyntheticEvent<HTMLInputElement>) => event.preventDefault(),
  };

  const sessionId = activeSession?.id ?? null;
  React.useEffect(() => {
    if (!sessionId || !employeeSession) {
      setZReport(null);
      return;
    }
    let cancelled = false;
    setLoadingZ(true);
    reportsService
      .getZ(tenantId, sessionId, employeeSession)
      .then((report) => { if (!cancelled) setZReport(report); })
      .catch(() => { if (!cancelled) setZReport(null); })
      .finally(() => { if (!cancelled) setLoadingZ(false); });
    return () => { cancelled = true; };
  }, [sessionId, tenantId, employeeSession]);

  // Pre-fills the counted-cash input from the saved count once per dialog open; stays editable.
  React.useEffect(() => {
    if (!closeDialogOpen) {
      prefillDoneRef.current = false;
      return;
    }
    if (!shiftCloseCashBalancesEnabled || !sessionId || prefillDoneRef.current) {
      return;
    }
    prefillDoneRef.current = true;
    let cancelled = false;
    const apply = (count: SessionCashCountView | null) => {
      if (!cancelled && count) setClosingCashInput((current) => (current === '' ? String(count.countedTotal) : current));
    };
    if (cashCount) {
      apply(cashCount);
    } else {
      sessionCashCountService.get(tenantId, sessionId, employeeSession).then(apply).catch(() => undefined);
    }
    return () => { cancelled = true; };
  }, [closeDialogOpen, shiftCloseCashBalancesEnabled, sessionId, cashCount, tenantId, employeeSession]);

  // Best-effort Z print from a pre-captured image; failure never blocks close (reprintable later).
  const printZToPrinter = async (printer: PrinterView, image: string | undefined) => {
    try {
      if (!image) return;
      // Session close invalidates the employee slot before this request is sent.
      await printersService.printReport(tenantId, Number(printer.id), image);
      notifications.show({
        color: 'green',
        title: 'הדפסה',
        message: 'דוח Z נשלח להדפסה!',
        autoClose: 3000,
      });
    } catch {
      // Close must not block on print failure; the report stays reprintable from the reports menu.
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: 'שליחת דוח Z להדפסה נכשלה',
        autoClose: 3000,
      });
    } finally {
      onZPrintInFlightChange?.(false);
    }
  };

  const notifyZNotReady = () => {
    notifications.show({
      color: 'red',
      title: 'דוח Z לא מוכן',
      message: 'הקופה לא נסגרה. המתינו לטעינת הדוח ונסו שוב.',
      autoClose: 5000,
    });
  };

  const parseCashInput = (value: string, label: string): number | undefined => {
    const normalized = value.trim();
    if (!normalized) {
      return undefined;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 999999) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: `${label} חייב להיות מספר בין 0 ל-999999`,
        autoClose: 3000,
      });
      return Number.NaN;
    }
    return parsed;
  };

  const closePayloadFromInputs = (): { closingCash?: number; nextOpeningCash?: number } | null => {
    const closingCash = parseCashInput(closingCashInput, 'ספירת מזומן סגירה');
    if (Number.isNaN(closingCash)) {
      return null;
    }
    const nextOpeningCash = parseCashInput(nextOpeningCashInput, 'מזומן פתיחה למשמרת הבאה');
    if (Number.isNaN(nextOpeningCash)) {
      return null;
    }
    return {
      ...(closingCash !== undefined ? { closingCash } : {}),
      ...(nextOpeningCash !== undefined ? { nextOpeningCash } : {}),
    };
  };

  const parsePreviewCashInput = (value: string): number | null => {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 999999) {
      return null;
    }
    return parsed;
  };

  const effectiveZReport = React.useMemo(() => {
    if (!zReport) {
      return null;
    }
    if (!shiftCloseCashBalancesEnabled) {
      return zReport;
    }
    const previewClosingCash = parsePreviewCashInput(closingCashInput);
    return {
      ...zReport,
      shiftCloseCashBalancesEnabled: true,
      closingCash: previewClosingCash,
    };
  }, [zReport, shiftCloseCashBalancesEnabled, closingCashInput]);

  // Route a close outcome: only a confirmed close prints Z; open-orders shows the guard.
  const finishCloseOutcome = (outcome: SessionCloseOutcome | void, target: PrinterView | null, image: string | undefined): void => {
    if (outcome && outcome.status === 'open-orders') {
      onZPrintInFlightChange?.(false);
      setConflictOpenOrderCount(outcome.openOrderCount ?? openOrderCount);
      setShowOpenOrdersGuard(true);
      return;
    }
    if (outcome && outcome.status === 'error') {
      onZPrintInFlightChange?.(false);
      return;
    }
    if (target) {
      notifications.show({
        color: 'green',
        title: 'הדפסה',
        message: 'דוח Z נשלח להדפסה!',
        autoClose: 3000,
      });
    }
    onZPrintInFlightChange?.(false);
  };

  const buildZReportPrint = (printerId: number, image: string): ZReportPrintRequest => ({
    printerId, printableImageDataUrl: image,
    ...(zReportEmailRecipients.length ? { pdfBase64: receiptPdfBase64(image) } : {}),
  });

  const handleCloseSessionClick = async () => {
    if (closingRef.current) return;
    if (openOrderCount > 0) {
      setShowOpenOrdersGuard(true);
      return;
    }
    if (!zReport || !printRef.current) {
      notifyZNotReady();
      return;
    }
    closingRef.current = true;
    try {
      let target: PrinterView | null = null;
      let image: string | undefined;
      try {
        const printers = await printersService.listActive(tenantId, employeeSession);
        // Z chain: sole billable → fixed → sole active; 2+ billable picks among billables only.
        const printTarget = resolveReceiptPrintTarget(printers);
        if (printTarget.kind === 'pick') {
          setPickerMode('close-with-print');
          setPickerPrinters(printTarget.candidates ?? printers);
          return;
        }
        if (printTarget.kind === 'print') {
          target = printers.find((p) => p.id === printTarget.printerId) ?? null;
        }
        if (target) {
          // Capture the Z while still mounted; closing unmounts printRef before html2canvas runs.
          image = await captureReceiptSnapshot(printRef.current);
        }
      } catch {
        notifyZNotReady();
        return;
      }
      if (!target || !image) {
        notifyZNotReady();
        return;
      }
      // Raised BEFORE the close: the session-closed refresh broadcast must not reload this device
      // while the Z print POST is still in flight (the reload kills the request silently).
      onZPrintInFlightChange?.(true);
      // Z prints only after a confirmed close; the threaded printer also opens the drawer we print from.
      finishCloseOutcome(await onCloseSession({
        cashDrawerPrinterId: target.id,
        zReportPrint: buildZReportPrint(target.id, image),
      }), target, image);
    } finally {
      closingRef.current = false;
    }
  };

  const handlePrintOnlyClick = async () => {
    if (closingRef.current || !zReport || !printRef.current) {
      return;
    }
    closingRef.current = true;
    try {
      const printers = await printersService.listActive(tenantId, employeeSession);
      const printTarget = resolveReceiptPrintTarget(printers);
      if (printTarget.kind === 'pick') {
        setPickerMode('print-only');
        setPickerPrinters(printTarget.candidates ?? printers);
        return;
      }
      if (printTarget.kind !== 'print') {
        return;
      }
      const target = printers.find((p) => p.id === printTarget.printerId) ?? null;
      if (!target || !printRef.current) {
        return;
      }
      const image = await captureReceiptSnapshot(printRef.current);
      await printZToPrinter(target, image);
    } finally {
      closingRef.current = false;
    }
  };

  const handleCloseWithoutPrint = async () => {
    if (closingRef.current) {
      return;
    }
    if (openOrderCount > 0) {
      setShowOpenOrdersGuard(true);
      return;
    }
    const payload = closePayloadFromInputs();
    if (payload === null) {
      return;
    }
    closingRef.current = true;
    try {
      finishCloseOutcome(await onCloseSession(payload), null, undefined);
      setCloseDialogOpen(false);
    } finally {
      closingRef.current = false;
    }
  };

  const handleCloseWithPrint = async () => {
    if (closingRef.current || !zReport || !printRef.current) {
      return;
    }
    if (openOrderCount > 0) {
      setShowOpenOrdersGuard(true);
      return;
    }
    const payload = closePayloadFromInputs();
    if (payload === null) {
      return;
    }
    closingRef.current = true;
    try {
      const printers = await printersService.listActive(tenantId, employeeSession);
      const printTarget = resolveReceiptPrintTarget(printers);
      if (printTarget.kind === 'pick') {
        setPickerMode('close-with-print');
        setPickerPrinters(printTarget.candidates ?? printers);
        return;
      }
      let target: PrinterView | null = null;
      let image: string | undefined;
      if (printTarget.kind === 'print') {
        target = printers.find((p) => p.id === printTarget.printerId) ?? null;
      }
      if (target && printRef.current) {
        image = await captureReceiptSnapshot(printRef.current);
      }
      if (!target || !image) {
        notifyZNotReady();
        return;
      }
      onZPrintInFlightChange?.(true);
      finishCloseOutcome(await onCloseSession({
        ...payload,
        cashDrawerPrinterId: target.id,
        zReportPrint: buildZReportPrint(target.id, image),
      }), target, image);
      setCloseDialogOpen(false);
    } finally {
      closingRef.current = false;
    }
  };

  const handlePickZPrinter = async (printerId: number) => {
    const target = pickerPrinters?.find((p) => p.id === printerId) ?? null;
    setPickerPrinters(null);
    const mode = pickerMode;
    setPickerMode(null);
    if (!target) {
      return;
    }
    let image: string | undefined;
    if (printRef.current) {
      localStorage.setItem(REPORT_PRINTER_STORAGE_KEY, String(target.id));
      image = await captureReceiptSnapshot(printRef.current);
    }
    if (mode === 'print-only') {
      await printZToPrinter(target, image);
      return;
    }
    if (mode === 'close-with-print') {
      const payload = closePayloadFromInputs();
      if (payload === null) {
        return;
      }
      if (!image) {
        notifyZNotReady();
        return;
      }
      onZPrintInFlightChange?.(true);
      finishCloseOutcome(await onCloseSession({
        ...payload,
        cashDrawerPrinterId: target.id,
        zReportPrint: buildZReportPrint(target.id, image),
      }), target, image);
      setCloseDialogOpen(false);
    }
  };

  // Cancelling the printer picker only dismisses it; the session must NOT close.
  const handleCancelZPicker = () => {
    setPickerPrinters(null);
    setPickerMode(null);
  };

  return (
    <section className={classes.shell} dir="rtl">
      <header className={classes.header}>
        <h3 className={classes.title}>סגירת קופה</h3>
        <div className={classes.stats}>
          <span>
            <strong>{orderCount}</strong> הזמנות
          </span>
          <span>
            מכירות כולל טיפים: <strong className={classes.statValue}>₪ {formatMoney(salesTotal)}</strong>
          </span>
        </div>
      </header>

      <div className={classes.preview} role="presentation">
        {loadingZ ? (
          <div className={classes.previewLoading}><Loader size="sm" /></div>
        ) : effectiveZReport ? (
          <PosReportView report={effectiveZReport} variant="screen" />
        ) : (
          <span className={classes.previewPlaceholder}>דוח Z יוצג כאן</span>
        )}
      </div>

      {effectiveZReport && (
        <div className={classes.offscreen} aria-hidden="true">
          <PosReportView report={effectiveZReport} variant="print" innerRef={printRef} />
        </div>
      )}

      <div className={classes.actions}>
        <div className={classes.actionsEnd}>
          <Button variant="cancel" onClick={onCancel} disabled={isSavingSession} dataTestId="session-close-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            onClick={() => setCashCountOpen(true)}
            disabled={!activeSession || isSavingSession || !hasEmployeeSession || !zReport}
            dataTestId="session-close-cash-count"
          >
            ספירת שטרות
          </Button>
          {shiftCloseCashBalancesEnabled ? (
            <>
              <Button
                variant="save"
                onClick={() => void handlePrintOnlyClick()}
                disabled={!activeSession || isSavingSession || isLoadingSession || !hasEmployeeSession || !zReport}
                dataTestId="session-close-print-only"
              >
                {isSavingSession ? 'מדפיס...' : 'הדפס'}
              </Button>
              <Button
                variant="save"
                onClick={() => setCloseDialogOpen(true)}
                disabled={!activeSession || isSavingSession || isLoadingSession || !hasEmployeeSession}
                dataTestId="session-close-open-close-dialog"
              >
                סגור משמרת
              </Button>
            </>
          ) : (
            <Button
              variant="save"
              onClick={() => void handleCloseSessionClick()}
              disabled={!activeSession || isSavingSession || isLoadingSession || loadingZ || !hasEmployeeSession}
              dataTestId="session-close-confirm"
            >
              {isSavingSession ? 'סוגר קופה...' : loadingZ ? 'טוען דוח Z...' : 'סגור קופה'}
            </Button>
          )}
        </div>
      </div>

      <Modal
        opened={closeDialogOpen}
        onClose={() => {
          if (!isSavingSession) {
            setCloseDialogOpen(false);
          }
        }}
        centered
        radius="md"
        size="md"
        title="סגירת משמרת"
      >
        <div className={classes.guard} dir="rtl">
          <TextInput
            data-testid="session-close-closing-cash"
            label="ספירת מזומן סגירה"
            placeholder="לדוגמה: 450"
            value={closingCashInput}
            onChange={(event) => setClosingCashInput(event.currentTarget.value)}
            inputMode="decimal"
            {...NO_SELECTION_INPUT_PROPS}
          />
          <TextInput
            data-testid="session-close-next-opening-cash"
            label="מזומן פתיחה למשמרת הבאה"
            placeholder="לדוגמה: 200"
            value={nextOpeningCashInput}
            onChange={(event) => setNextOpeningCashInput(event.currentTarget.value)}
            inputMode="decimal"
            {...NO_SELECTION_INPUT_PROPS}
          />
          <div className={classes.closeDialogActions} data-testid="session-close-dialog-actions">
            <Button
              variant="save"
              onClick={() => void handleCloseWithPrint()}
              disabled={isSavingSession || !zReport}
              fullWidth
              dataTestId="session-close-with-print"
            >
              סגור משמרת והדפס Z
            </Button>
            <Button
              variant="save"
              onClick={() => void handleCloseWithoutPrint()}
              disabled={isSavingSession}
              fullWidth
              dataTestId="session-close-without-print"
            >
              סגור משמרת ללא הדפסת Z
            </Button>
            <Button
              variant="cancel"
              onClick={() => setCloseDialogOpen(false)}
              disabled={isSavingSession}
              fullWidth
              dataTestId="session-close-dialog-cancel"
            >
              ביטול
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={showOpenOrdersGuard}
        onClose={() => { setShowOpenOrdersGuard(false); setConflictOpenOrderCount(0); }}
        onContextMenu={(event) => event.preventDefault()}
        centered
        radius="md"
        withCloseButton={false}
        size="sm"
      >
        <div className={classes.guard} dir="rtl">
          <p className={classes.guardText}>לא ניתן לסגור קופה</p>
          <p className={classes.guardSubText}>
            {Math.max(openOrderCount, conflictOpenOrderCount)} הזמנות עדיין פתוחות, עבור לעמוד הזמנות?
          </p>
          <div className={classes.actionsEnd}>
            <Button variant="cancel" onClick={() => { setShowOpenOrdersGuard(false); setConflictOpenOrderCount(0); }} dataTestId="session-close-guard-cancel">
              ביטול
            </Button>
            <Button
              variant="save"
              onClick={() => {
                setShowOpenOrdersGuard(false);
                onGoToOrders();
              }}
              dataTestId="session-close-guard-go"
            >
              עבור
            </Button>
          </div>
        </div>
      </Modal>

      {cashCountOpen && activeSession && (
        <CashCountModal
          onClose={() => setCashCountOpen(false)}
          tenantId={tenantId}
          employeeSession={employeeSession}
          sessionId={activeSession.id}
          sessionName={activeSession.name}
          expectedCash={zReport ? expectedCashFromReport(zReport) : null}
          header={zReport?.header ?? ''}
          onSaved={setCashCount}
        />
      )}

      <ReportPrinterPickModal
        opened={pickerPrinters !== null}
        printers={pickerPrinters ?? []}
        onConfirm={(printerId) => void handlePickZPrinter(printerId)}
        onClose={handleCancelZPicker}
      />
    </section>
  );
};

export const SessionControlsSection = React.memo(SessionControlsSectionComponent);
